<template>
  <div>
    <h2 class="mb-3">
      {{ $t("exportPrompt.wordOptions") }}
    </h2>
    <div class="d-flex flex-row mb-3">
      <h4 class="left-side">{{ $t("exportPrompt.template") }}</h4>
      <drop-down
          :initial-active-value="template[0]"
          :labels="template"
          :values="template"
          class="w-75"
      />
    </div>

    <image-quality-selector v-model="imageQuality" />
    <div v-if="finishedMessage" class="form-error p-1">{{ finishedMessage }}</div>

    <div class="generate-legend-box">
      <div class="mb-2 lighter">{{ $t('AdditionalOptions') }}</div>
      <label for="generateLegend" class="container vform-editor-body ml-0">
        {{ $t("exportPrompt.autoGenerateLegend") }}
        <input
            id="generateLegend"
            type="checkbox"
            v-model="autoGenerateLegend"
        >
        <span class="checkmark" />
      </label>
    </div>
    <template v-if="autoGenerateLegend">
      <!--DO NOT REMOVE THE FOLLOWING in case we want to use it later in life-->
      <div class="lighter mb-2 mt-4">
        {{ $t("exportPrompt.pleaseSelectAlternativeNames") }}
      </div>
      <div class="d-flex flex-row mb-3">
        <h4 class="left-side">{{ $t("exportPrompt.metaSet") }}</h4>
        <drop-down
            v-if="metaSetIds.length"
            :labels="metaSetNames"
            :values="metaSetIds"
            @select="(selected) => (selectedMetaSet = selected)"
            class="w-75"
        />
      </div>

      <div v-if="selectedMetaSet" class="d-flex flex-row mb-3">
        <h4 class="left-side">{{ $t("exportPrompt.metaField") }}</h4>
        <drop-down
            :labels="metaFieldDescriptions"
            :values="metaFieldIds"
            @select="(selected) => (selectedMetaField = selected)"
            class="w-75"
        />
      </div>
    </template>

    <label v-if="autoGenerateLegend" for="generatePartList" class="container vform-editor-body ml-0">
      {{ $t("exportPrompt.generatePartList") }}
      <input
          id="generatePartList"
          type="checkbox"
          v-model="generatePartList"
      >
      <span class="checkmark" />
    </label>

    <div
        :class="['settings-button float-right d-inline orange', isExporting ? 'disabled' : '']"
        @click="exportToWord()"
    >
      {{ $t("exportPrompt.download") }}
    </div>
  </div>
</template>

<script>
import DropDown from "@/components/forms/DropDown.vue";
import {mapActions} from "vuex";
import toWordMixinJs from "@/components/vForm/mixins/toWordMixin.js.vue";
import vFormAndProjectMixin from "@/components/vForm/mixins/vFormAndProjectMixin.js.vue";
import ElementMethodMixinJs from "@/components/vForm/mixins/ElementMethodMixin.js.vue";
import {PlainLanguages} from "@/enum";
import ImageQualitySelector from './ImageQualitySelector.vue';
import {DEFAULT_EXPORT_IMAGE_QUALITY} from './exportImageQuality';
export default {
  name: "WordExport",
  components: {DropDown, ImageQualitySelector},
  mixins: [ElementMethodMixinJs, vFormAndProjectMixin, toWordMixinJs],
  props: {
    metaSets: {type: Array, default: () => []},
    template: {type: Array, default: () => ['Default Template']},
    projectId: {type: String, required: true},
    organizationId: {type: String, required: true},
    slides: {type: Array, required: true},
    config: {type: Object, required: true},
    projectName: {type: String, required: true},
    projectLoadingMode: {type: String, required: true},
  },
  data() {
    return {
      autoGenerateLegend: false,
      imageQuality: DEFAULT_EXPORT_IMAGE_QUALITY,
      finishedMessage: '',
      isExporting: false,
      generatePartList: false,
      selectedMetaSet: null,
      selectedMetaField: null,
      metaFields: [],
      PlainLanguages
    }
  },
  computed: {
    metaSetNames() {
      if (this.metaSets.length > 0) {
        return Object.values(this.metaSets).map((metaSet) => metaSet.name);
      }
      return [];
    },
    metaSetIds() {
      if (this.metaSets.length > 0) {
        return Object.values(this.metaSets).map((metaSet) => metaSet.id);
      }
      return [];
    },
    metaFieldDescriptions() {
      if (this.metaFields.length > 0) {
        return Object.values(this.metaFields).map(
            (metaField) => metaField.description
        );
      }
      return [];
    },
    metaFieldIds() {
      if (this.metaFields.length > 0) {
        return Object.values(this.metaFields).map((metaField) => metaField.id);
      }
      return [];
    },
  },
  watch: {
    selectedMetaSet(val) {
      if (val) {
        const item = this.metaSets.find((metaSet) => {
          return metaSet.id === val;
        });
        if (item) {
          this.metaFields = item.metaFields;
          this.selectedMetaField = this.metaFields.length
              ? this.metaFields[0].id
              : null;
        } else {
          this.metaFields = [];
        }
      }
    },
  },
  methods: {
    ...mapActions([
      "updateCurrentWordPage",
      "updateTotalWordPages",
      "updateWORDGenerating",
    ]),
    currentLanguageDir() {
      const language = this.$store.getters.getSelectedFormLanguage;
      const languageObject = Object.values(this.PlainLanguages).find(lang => lang.name === language);
      return languageObject && languageObject.dir ? languageObject.dir : 'ltr';
    },
    resetMetaSet() {
      this.selectedMetaSet = null;
      this.selectedMetaField = null;
      this.metaFields = [];
    },
    async exportToWord() {
      if (this.isExporting) return;
      this.isExporting = true;
      const exportConfig = {
        autoGenerateLegend: this.autoGenerateLegend,
        generatePartList: this.generatePartList,
        metaSetId: this.selectedMetaSet,
        metaField: this.selectedMetaField,
        isFormRTL: this.currentLanguageDir() === 'rtl',
        imageQuality: this.imageQuality,
      }
      await this.updateWORDGenerating(true)
      this.finishedMessage = '';
      try {
        await this.toWord(this.organizationId, this.projectId, this.slides, this.config, exportConfig);
        this.resetMetaSet();
      } catch (error) {
        this.finishedMessage = this.$t('exportPrompt.exportFailed');
      } finally {
        this.isExporting = false;
        await this.updateWORDGenerating(false);
      }
    },
  }
}
</script>
